import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { tokens } from '../../theme';
import { brandApi, type BrandProfile, type BrandCard } from '../../lib/api';
import { Button } from '../../components/Button';
import { Badge } from '../../components/Badge';
import { Skeleton } from '../../components/Skeleton';

interface BrandCampaignRow {
  id: string;
  title: string;
  status: string;
  budget_total: number;       // cents
  budget_spent: number;       // cents
  cpm_rate: number | null;
  vertical: string | null;
  starts_at: string | null;
  ends_at: string | null;
  deposit_paid_at: string | null;
  final_paid_at: string | null;
  completed_at: string | null;
  deposit_due_cents: number;
  final_due_cents: number;
}

interface Data {
  campaigns: BrandCampaignRow[];
  review_status: string;
  profile: BrandProfile | null;
}

function money(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

// A campaign counts as "launched" once the deposit is paid or it's live.
// 'submitted' / 'draft' / 'awaiting_deposit' are pre-payment, so they don't
// count. Drives the guided-setup step 3.
function isLaunched(c: BrandCampaignRow): boolean {
  return (
    !!c.deposit_paid_at ||
    ['active', 'awaiting_activation', 'paid', 'completed'].includes(c.status)
  );
}

export function Home() {
  const navigate = useNavigate();
  const [data, setData] = useState<Data | null>(null);
  const [card, setCard] = useState<BrandCard | null | undefined>(undefined); // undefined = loading

  useEffect(() => {
    brandApi
      .campaigns()
      .then((d) => {
        const typed = d as unknown as Data;
        // First-login redirect: brand exists but hasn't completed the welcome
        // wizard yet. Send them to /app/welcome before the dashboard.
        if (typed.profile && !typed.profile.onboarded_at) {
          navigate('/app/welcome', { replace: true });
          return;
        }
        setData(typed);
      })
      .catch(() => setData({ campaigns: [], review_status: 'pending', profile: null }));
    // Card-on-file status drives the guided-setup checklist. Best-effort:
    // a failure just shows the "add card" step rather than blocking the page.
    brandApi
      .paymentMethod()
      .then((r) => setCard(r.card))
      .catch(() => setCard(null));
  }, [navigate]);

  if (!data) {
    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} height={88} radius={12} />
          ))}
        </div>
        <Skeleton height={32} width="40%" />
        <div style={{ marginTop: 24 }}>
          <Skeleton height={120} />
        </div>
      </div>
    );
  }

  const pending = data.review_status === 'pending';
  const campaigns = data.campaigns;

  const totals = campaigns.reduce(
    (acc, c) => {
      acc.spent += c.budget_spent || 0;
      acc.budget += c.budget_total || 0;
      if (c.status === 'active') acc.active += 1;
      return acc;
    },
    { spent: 0, budget: 0, active: 0 }
  );
  const remaining = Math.max(0, totals.budget - totals.spent);

  // Guided setup: shown until the brand has a card on file, a first brief, and
  // a launched campaign. Once all three are true it disappears for good. The
  // source of truth is real state (Stripe card + campaign rows), not a flag.
  const hasCard = card === undefined ? undefined : card !== null;
  const hasBrief = campaigns.length > 0;
  const hasLaunched = campaigns.some(isLaunched);
  const setupComplete = hasCard === true && hasBrief && hasLaunched;
  const firstName = (data.profile?.name ?? '').split(/[\s,]/)[0] || 'there';

  return (
    <div>
      {!setupComplete && (
        <GuidedSetup
          brandName={data.profile?.name ?? null}
          firstName={firstName}
          hasCard={hasCard}
          hasBrief={hasBrief}
          hasLaunched={hasLaunched}
          pending={pending}
          firstCampaignId={campaigns[0]?.id ?? null}
        />
      )}

      {/* Stat row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 16,
          marginBottom: 24,
        }}
      >
        <StatCard label="Remaining budget" value={money(remaining)} accent />
        <StatCard label="Campaigns" value={String(campaigns.length)} />
        <StatCard label="Active" value={String(totals.active)} />
        <StatCard label="Spent" value={money(totals.spent)} />
      </div>

      {pending && (
        <div
          style={{
            padding: 16,
            background: tokens.color.paper[100],
            border: `1px solid ${tokens.color.line[100]}`,
            borderLeftWidth: 3,
            borderLeftColor: tokens.color.accent.sage,
            borderRadius: 8,
            marginBottom: 24,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <div>
            <strong style={{ color: tokens.color.ink[900], fontSize: 13 }}>
              Account under review
            </strong>
            <p style={{ fontSize: 13, color: tokens.color.ink[700], marginTop: 4 }}>
              We'll be in touch shortly. New briefs are paused until approval.
            </p>
          </div>
          <a href="mailto:hello@imorbis.com">
            <Button size="sm" variant="secondary">
              Email us
            </Button>
          </a>
        </div>
      )}

      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 16,
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <h2
          style={{
            fontFamily: tokens.font.display,
            fontSize: 28,
            fontWeight: 600,
            letterSpacing: '-0.02em',
            color: tokens.color.ink[900],
          }}
        >
          Campaigns
        </h2>
        <Link to="/app/brief">
          <Button size="md" disabled={pending}>
            + New campaign
          </Button>
        </Link>
      </header>

      {campaigns.length === 0 ? (
        <div
          style={{
            padding: 64,
            textAlign: 'center',
            background: tokens.color.paper['00'],
            border: `1px dashed ${tokens.color.line[100]}`,
            borderRadius: 12,
          }}
        >
          <span
            style={{ fontFamily: tokens.font.display, fontWeight: 600, letterSpacing: '-0.02em', fontSize: 24, color: tokens.color.ink[700] }}
          >
            No campaigns yet.
          </span>
          <p style={{ marginTop: 12, fontSize: 14, color: tokens.color.ink[500], marginBottom: 24 }}>
            Create your first campaign to start distributing content.
          </p>
          {!pending && (
            <Link to="/app/brief">
              <Button size="md">+ New campaign</Button>
            </Link>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {campaigns.map((c) => (
            <CampaignRow key={c.id} c={c} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Guided setup ────────────────────────────────────────────────────────────
// Premium first-run checklist. Walks a new brand from card-on-file to a live
// campaign, then retires itself.

function GuidedSetup({
  brandName,
  firstName,
  hasCard,
  hasBrief,
  hasLaunched,
  pending,
  firstCampaignId,
}: {
  brandName: string | null;
  firstName: string;
  hasCard: boolean | undefined;
  hasBrief: boolean;
  hasLaunched: boolean;
  pending: boolean;
  firstCampaignId: string | null;
}) {
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const done = [hasCard === true, hasBrief, hasLaunched].filter(Boolean).length;
  const total = 3;
  const pct = Math.round((done / total) * 100);

  async function addCard() {
    setAdding(true);
    setErr(null);
    try {
      const { url } = await brandApi.setupCardSession();
      window.location.href = url; // hand off to Stripe's hosted setup page
    } catch {
      setErr('Could not open payment setup. Try again, or email hello@imorbis.com.');
      setAdding(false);
    }
  }

  return (
    <section
      style={{
        background: tokens.color.paper['00'],
        border: `1px solid ${tokens.color.line[100]}`,
        borderRadius: 16,
        padding: 32,
        marginBottom: 28,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${tokens.color.ink[900]}, ${tokens.color.accent.tan})`,
        }}
      />
      <div
        style={{
          fontFamily: tokens.font.mono,
          fontSize: 11,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: tokens.color.ink[500],
          marginBottom: 10,
        }}
      >
        Getting started
      </div>
      <h1
        style={{
          fontFamily: tokens.font.display,
          fontSize: 'clamp(26px, 3.4vw, 34px)',
          fontWeight: 600,
          letterSpacing: '-0.025em',
          color: tokens.color.ink[900],
          lineHeight: 1.08,
          margin: 0,
        }}
      >
        Welcome to Dreamlink Studio{brandName ? `, ${brandName}` : ''}.
      </h1>
      <p style={{ marginTop: 12, fontSize: 16, lineHeight: 1.55, color: tokens.color.ink[700], maxWidth: '54ch' }}>
        {firstName}, three quick steps to get your first campaign live. We handle the rest.
      </p>

      <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Step
          n={1}
          title="Set up payment"
          body="Add a card once, held securely by Stripe. Campaign deposits draw from it, so you never re-enter card details."
          status={hasCard === undefined ? 'loading' : hasCard ? 'done' : 'active'}
          action={
            hasCard === true ? undefined : (
              <Button size="sm" onClick={addCard} loading={adding} disabled={pending}>
                Add card
              </Button>
            )
          }
        />
        <Step
          n={2}
          title="Create your first brief"
          body="Tell us the artist, budget, and goal. We turn it into a live distribution campaign."
          status={hasBrief ? 'done' : hasCard === true ? 'active' : 'todo'}
          action={
            hasBrief ? undefined : (
              <Button
                size="sm"
                variant={hasCard === true ? 'primary' : 'secondary'}
                disabled={pending}
                onClick={() => navigate('/app/brief')}
              >
                Start brief
              </Button>
            )
          }
        />
        <Step
          n={3}
          title="Launch your campaign"
          body="Pay the deposit and we put your campaign in front of the creator network."
          status={hasLaunched ? 'done' : hasBrief ? 'active' : 'todo'}
          action={
            hasLaunched || !hasBrief || !firstCampaignId ? undefined : (
              <Button size="sm" onClick={() => navigate(`/app/campaigns/${firstCampaignId}`)}>
                Review and launch
              </Button>
            )
          }
        />
      </div>

      {err && <p style={{ marginTop: 16, fontSize: 13, color: tokens.color.accent.rust }}>{err}</p>}

      {/* Progress */}
      <div style={{ marginTop: 26, display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ flex: 1, height: 6, borderRadius: 999, background: tokens.color.paper[200], overflow: 'hidden' }}>
          <div
            style={{
              width: `${pct}%`,
              height: '100%',
              background: `linear-gradient(90deg, ${tokens.color.ink[900]}, ${tokens.color.accent.tan})`,
              transition: 'width 360ms cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          />
        </div>
        <span style={{ fontFamily: tokens.font.mono, fontSize: 12, color: tokens.color.ink[500], whiteSpace: 'nowrap' }}>
          {done} of {total} complete
        </span>
      </div>
    </section>
  );
}

function Step({
  n,
  title,
  body,
  status,
  action,
}: {
  n: number;
  title: string;
  body: string;
  status: 'todo' | 'active' | 'done' | 'loading';
  action?: React.ReactNode;
}) {
  const done = status === 'done';
  const active = status === 'active';
  return (
    <div
      style={{
        display: 'flex',
        gap: 16,
        alignItems: 'flex-start',
        padding: 16,
        borderRadius: 12,
        background: active ? tokens.color.paper[50] : 'transparent',
        border: `1px solid ${active ? tokens.color.line[100] : 'transparent'}`,
        opacity: status === 'todo' ? 0.6 : 1,
        transition: 'background 200ms, opacity 200ms',
      }}
    >
      <Marker n={n} done={done} active={active} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: tokens.font.display,
            fontSize: 16,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            color: tokens.color.ink[900],
          }}
        >
          {title}
        </div>
        <p style={{ marginTop: 4, fontSize: 13.5, lineHeight: 1.5, color: tokens.color.ink[500] }}>{body}</p>
      </div>
      {action && <div style={{ flexShrink: 0 }}>{action}</div>}
    </div>
  );
}

function Marker({ n, done, active }: { n: number; done: boolean; active: boolean }) {
  return (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: done ? tokens.color.accent.tan : active ? tokens.color.ink[900] : 'transparent',
        border: done || active ? 'none' : `1.5px solid ${tokens.color.line[100]}`,
        color: done ? tokens.color.ink[900] : active ? '#fff' : tokens.color.ink[500],
        fontFamily: tokens.font.mono,
        fontSize: 13,
        fontWeight: 600,
        boxShadow: active ? `0 0 0 4px rgba(94,252,141,0.18)` : 'none',
      }}
    >
      {done ? (
        <svg width="14" height="14" viewBox="0 0 22 22" fill="none">
          <path d="M5 11.5l4 4 8-9" stroke="#08080A" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        n
      )}
    </div>
  );
}

function StatCard({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div
      style={{
        padding: 16,
        background: tokens.color.paper['00'],
        border: `1px solid ${tokens.color.line[100]}`,
        borderRadius: 12,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {accent && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg, ${tokens.color.ink[500]}, ${tokens.color.accent.tan})`,
          }}
        />
      )}
      <div
        style={{
          fontFamily: tokens.font.mono,
          fontSize: 11,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: tokens.color.ink[500],
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: tokens.font.display,
          fontSize: 28,
          fontWeight: 600,
          color: tokens.color.ink[900],
          letterSpacing: '-0.02em',
        }}
      >
        {value}
      </div>
    </div>
  );
}

function CampaignRow({ c }: { c: BrandCampaignRow }) {
  const budget = c.budget_total || 0;
  const spent = c.budget_spent || 0;
  const pct = budget > 0 ? Math.min(100, (spent / budget) * 100) : 0;
  const status = c.status as Parameters<typeof Badge>[0]['status'];

  return (
    <Link
      to={`/app/campaigns/${c.id}`}
      style={{
        display: 'block',
        padding: 18,
        background: tokens.color.paper['00'],
        border: `1px solid ${
          c.status === 'awaiting_activation' || c.status === 'awaiting_deposit'
            ? 'rgba(94,252,141,0.40)'
            : tokens.color.line[100]
        }`,
        borderRadius: 12,
        transition: 'border-color 240ms, transform 240ms cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <span
          style={{
            fontFamily: tokens.font.display,
            fontSize: 20,
            fontWeight: 600,
            letterSpacing: '-0.015em',
            color: tokens.color.ink[900],
          }}
        >
          {c.title}
        </span>
        <Badge status={status} />
      </div>

      <div
        style={{
          display: 'flex',
          gap: 24,
          fontSize: 13,
          color: tokens.color.ink[500],
          flexWrap: 'wrap',
        }}
      >
        {c.cpm_rate && c.cpm_rate > 0 && (
          <span>
            <span style={{ fontFamily: tokens.font.mono }}>${c.cpm_rate.toFixed(2)}</span> CPM
          </span>
        )}
        <span>
          <span style={{ fontFamily: tokens.font.mono, color: tokens.color.ink[900] }}>
            {money(spent)}
          </span>{' '}
          / <span style={{ fontFamily: tokens.font.mono }}>{money(budget)}</span>
        </span>
        {c.vertical && <span>{c.vertical}</span>}
        {c.ends_at && <span>Ends {c.ends_at.slice(0, 10)}</span>}
      </div>

      {budget > 0 && (
        <div
          style={{
            marginTop: 12,
            height: 4,
            borderRadius: 2,
            background: tokens.color.paper[200],
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: '100%',
              background: `linear-gradient(90deg, ${tokens.color.ink[500]}, ${tokens.color.accent.tan})`,
            }}
          />
        </div>
      )}
    </Link>
  );
}
